import { useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, BACKEND_URL } from "../../App";
import { 
  Save, Upload, Image, Video, X, Eye, EyeOff,
  Plus, User, Users, Heart, MessageCircle, Camera
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function CouplePageEditor() {
  const { wedding, setWedding } = useOutletContext();
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [formData, setFormData] = useState({
    // Basic Info
    couple_name_1: wedding?.couple_name_1 || "",
    couple_name_2: wedding?.couple_name_2 || "",
    wedding_date: wedding?.wedding_date || "",
    slug: wedding?.slug || "",
    // Hero Section
    hero_image: wedding?.hero_image || "",
    subtitle_text: wedding?.subtitle_text || "",
    // Same Day Edit
    sde_video: wedding?.sde_video || "",
    // Vows
    groom_vow_text: wedding?.groom_vow_text || "",
    bride_vow_text: wedding?.bride_vow_text || "",
    // Gratitude
    gratitude_video: wedding?.gratitude_video || "",
    gratitude_text: wedding?.gratitude_text || "",
    // Gallery
    gallery_images: wedding?.gallery_images || [],
    // Tree Settings
    tree_enabled: wedding?.tree_enabled ?? true,
    tree_moderation_mode: wedding?.tree_moderation_mode || "auto_publish",
    // Best Wishes
    best_wishes_preview_count: wedding?.best_wishes_preview_count || 6,
    // Visibility
    visibility: wedding?.visibility || "public",
    passcode: wedding?.passcode || "",
    // Sections
    sections_enabled: wedding?.sections_enabled || {
      hero: true, highlights: true, tree: true, gallery: true,
      vows: true, gratitude: true, best_wishes: true,
      perspectives: true, interactive_story: true, timeline: true
    },
    // Timeline
    timeline_events: wedding?.timeline_events || [],
  });
  const fileInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [uploading, setUploading] = useState(false);

  if (!wedding) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-wedding-ivory/60">No wedding assigned to your account.</p>
      </div>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API}/weddings/${wedding.wedding_id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWedding(response.data);
      toast.success("Changes saved successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isVideo = uploadingFor.includes("video");
    const allowedImageTypes = ["image/jpeg", "image/png", "image/webp"];
    const allowedVideoTypes = ["video/mp4"];
    
    if (isVideo && !allowedVideoTypes.includes(file.type)) {
      toast.error("Please upload an MP4 video file");
      return;
    }
    if (!isVideo && !allowedImageTypes.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, or WebP image");
      return;
    }

    // Check file size (50MB for video, 10MB for image)
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${isVideo ? "50MB" : "10MB"}`);
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);
    uploadData.append("wedding_id", wedding.wedding_id);
    uploadData.append("media_type", isVideo ? "video" : "image");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/media/upload`, uploadData, {
        headers: { 
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        }
      });
      
      if (uploadingFor === "hero_image") {
        setFormData({ ...formData, hero_image: response.data.url });
      } else if (uploadingFor === "sde_video") {
        setFormData({ ...formData, sde_video: response.data.url });
      } else if (uploadingFor === "gratitude_video") {
        setFormData({ ...formData, gratitude_video: response.data.url });
      } else if (uploadingFor === "gallery") {
        setFormData({ 
          ...formData, 
          gallery_images: [...formData.gallery_images, response.data.url] 
        });
      }
      
      toast.success("File uploaded successfully!");
    } catch (error) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      setUploadingFor(null);
    }
  };

  const triggerUpload = (field) => {
    setUploadingFor(field);
    fileInputRef.current?.click();
  };

  const removeGalleryImage = (index) => {
    const newImages = formData.gallery_images.filter((_, i) => i !== index);
    setFormData({ ...formData, gallery_images: newImages });
  };

  const getMediaUrl = (url) => {
    if (!url) return null;
    return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  };

  const sections = [
    { id: "basic", label: "Basic Info", icon: User },
    { id: "hero", label: "Hero Section", icon: Image },
    { id: "sde", label: "Same Day Edit", icon: Video },
    { id: "vows", label: "Wedding Vows", icon: Heart },
    { id: "gratitude", label: "Gratitude", icon: MessageCircle },
    { id: "gallery", label: "Photo Gallery", icon: Camera },
    { id: "timeline", label: "Timeline", icon: Users },
    { id: "tree", label: "Tree Settings", icon: Users },
    { id: "visibility", label: "Visibility", icon: Eye },
  ];

  return (
    <div className="space-y-6" data-testid="page-editor">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={uploadingFor?.includes("video") ? "video/mp4" : "image/jpeg,image/png,image/webp"}
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory">Edit Wedding Page</h1>
          <p className="text-wedding-ivory/60 mt-1">Manage your wedding content</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          data-testid="save-btn"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-wedding-black border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2 p-4 bg-wedding-charcoal rounded-sm">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm transition-colors ${
              activeSection === section.id
                ? "bg-wedding-gold text-wedding-black"
                : "text-wedding-ivory/60 hover:text-wedding-ivory hover:bg-white/5"
            }`}
          >
            <section.icon className="w-4 h-4" />
            <span className="text-sm">{section.label}</span>
          </button>
        ))}
      </div>

      {/* Form Sections */}
      <div className="form-section">
        {/* Basic Info */}
        {activeSection === "basic" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-xl text-wedding-ivory mb-6">Basic Information</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="form-label">Groom's First Name</Label>
                <Input
                  value={formData.couple_name_1}
                  onChange={(e) => setFormData({ ...formData, couple_name_1: e.target.value })}
                  placeholder="Jason"
                  className="input-premium mt-2"
                />
              </div>
              <div>
                <Label className="form-label">Bride's First Name</Label>
                <Input
                  value={formData.couple_name_2}
                  onChange={(e) => setFormData({ ...formData, couple_name_2: e.target.value })}
                  placeholder="Mara"
                  className="input-premium mt-2"
                />
              </div>
            </div>

            <div>
              <Label className="form-label">Wedding Date</Label>
              <Input
                value={formData.wedding_date}
                onChange={(e) => setFormData({ ...formData, wedding_date: e.target.value })}
                placeholder="December 25, 2025"
                className="input-premium mt-2"
              />
            </div>

            <div>
              <Label className="form-label">Page URL Slug</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.replace(/\s/g, "") })}
                placeholder="JasonAndMara2025"
                className="input-premium mt-2"
              />
              <p className="text-wedding-ivory/40 text-xs mt-1">
                Your page will be at: memoraorbs.com/{formData.slug || "your-slug"}
              </p>
            </div>
          </motion.div>
        )}

        {/* Hero Section */}
        {activeSection === "hero" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-xl text-wedding-ivory mb-6">Hero Section</h2>
            
            <div>
              <Label className="form-label">Hero Image</Label>
              <p className="text-wedding-ivory/40 text-xs mb-3">
                The main background image for your wedding page. Recommended: 1920x1080 or larger.
              </p>
              <div className="mt-2">
                {formData.hero_image ? (
                  <div className="relative aspect-video bg-wedding-subtle rounded-sm overflow-hidden max-w-xl">
                    <img
                      src={getMediaUrl(formData.hero_image)}
                      alt="Hero"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => setFormData({ ...formData, hero_image: "" })}
                      className="absolute top-2 right-2 p-2 bg-wedding-black/80 rounded-full hover:bg-wedding-black transition-colors"
                    >
                      <X className="w-4 h-4 text-wedding-ivory" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => triggerUpload("hero_image")}
                    disabled={uploading}
                    className="w-full max-w-xl aspect-video border border-dashed border-white/20 hover:border-wedding-gold/50 transition-colors flex flex-col items-center justify-center gap-3 rounded-sm"
                  >
                    {uploading && uploadingFor === "hero_image" ? (
                      <div className="w-8 h-8 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-wedding-ivory/40" />
                        <span className="text-wedding-ivory/60 text-sm">Upload Hero Image</span>
                        <span className="text-wedding-ivory/30 text-xs">JPG, PNG, or WebP (max 10MB)</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div>
              <Label className="form-label">Subtitle Text</Label>
              <p className="text-wedding-ivory/40 text-xs mb-3">
                A short romantic message displayed below your names.
              </p>
              <Textarea
                value={formData.subtitle_text}
                onChange={(e) => setFormData({ ...formData, subtitle_text: e.target.value })}
                placeholder="Two hearts, one love, forever together..."
                className="bg-transparent border border-white/20 focus:border-wedding-gold outline-none px-4 py-3 transition-colors duration-300 placeholder:text-white/20 text-wedding-ivory min-h-[100px] rounded-sm w-full"
              />
            </div>
          </motion.div>
        )}

        {/* Same Day Edit Video */}
        {activeSection === "sde" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-xl text-wedding-ivory mb-6">Same Day Edit Video</h2>
            
            <div>
              <Label className="form-label">SDE Video</Label>
              <p className="text-wedding-ivory/40 text-xs mb-3">
                Upload your Same Day Edit video. This will be featured prominently on your page.
              </p>
              <div className="mt-2">
                {formData.sde_video ? (
                  <div className="relative aspect-video bg-wedding-subtle rounded-sm overflow-hidden max-w-xl">
                    <video
                      src={getMediaUrl(formData.sde_video)}
                      className="w-full h-full object-cover"
                      controls
                    />
                    <button
                      onClick={() => setFormData({ ...formData, sde_video: "" })}
                      className="absolute top-2 right-2 p-2 bg-wedding-black/80 rounded-full hover:bg-wedding-black transition-colors"
                    >
                      <X className="w-4 h-4 text-wedding-ivory" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => triggerUpload("sde_video")}
                    disabled={uploading}
                    className="w-full max-w-xl aspect-video border border-dashed border-white/20 hover:border-wedding-gold/50 transition-colors flex flex-col items-center justify-center gap-3 rounded-sm"
                  >
                    {uploading && uploadingFor === "sde_video" ? (
                      <div className="w-8 h-8 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Video className="w-8 h-8 text-wedding-ivory/40" />
                        <span className="text-wedding-ivory/60 text-sm">Upload SDE Video</span>
                        <span className="text-wedding-ivory/30 text-xs">MP4 only (max 50MB)</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-wedding-subtle rounded-sm">
              <div>
                <span className="text-wedding-ivory">Show SDE Section</span>
                <p className="text-wedding-ivory/50 text-sm">Display the Same Day Edit on your page</p>
              </div>
              <Switch
                checked={formData.sections_enabled.highlights}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  sections_enabled: { ...formData.sections_enabled, highlights: checked }
                })}
              />
            </div>
          </motion.div>
        )}

        {/* Wedding Vows */}
        {activeSection === "vows" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-xl text-wedding-ivory mb-6">Wedding Vows</h2>
            
            <div>
              <Label className="form-label">{formData.couple_name_1 || "Groom"}'s Vows</Label>
              <Textarea
                value={formData.groom_vow_text}
                onChange={(e) => setFormData({ ...formData, groom_vow_text: e.target.value })}
                placeholder="Write your vows here..."
                className="bg-transparent border border-white/20 focus:border-wedding-gold outline-none px-4 py-3 transition-colors duration-300 placeholder:text-white/20 text-wedding-ivory min-h-[150px] rounded-sm w-full mt-2"
              />
            </div>

            <div>
              <Label className="form-label">{formData.couple_name_2 || "Bride"}'s Vows</Label>
              <Textarea
                value={formData.bride_vow_text}
                onChange={(e) => setFormData({ ...formData, bride_vow_text: e.target.value })}
                placeholder="Write your vows here..."
                className="bg-transparent border border-white/20 focus:border-wedding-gold outline-none px-4 py-3 transition-colors duration-300 placeholder:text-white/20 text-wedding-ivory min-h-[150px] rounded-sm w-full mt-2"
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-wedding-subtle rounded-sm">
              <div>
                <span className="text-wedding-ivory">Show Vows Section</span>
                <p className="text-wedding-ivory/50 text-sm">Display your vows on the wedding page</p>
              </div>
              <Switch
                checked={formData.sections_enabled.vows}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  sections_enabled: { ...formData.sections_enabled, vows: checked }
                })}
              />
            </div>
          </motion.div>
        )}

        {/* Gratitude Section */}
        {activeSection === "gratitude" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-xl text-wedding-ivory mb-6">Gratitude Section</h2>
            
            <div>
              <Label className="form-label">Gratitude Video</Label>
              <p className="text-wedding-ivory/40 text-xs mb-3">
                Upload a thank you video message for your guests.
              </p>
              <div className="mt-2">
                {formData.gratitude_video ? (
                  <div className="relative aspect-video bg-wedding-subtle rounded-sm overflow-hidden max-w-xl">
                    <video
                      src={getMediaUrl(formData.gratitude_video)}
                      className="w-full h-full object-cover"
                      controls
                    />
                    <button
                      onClick={() => setFormData({ ...formData, gratitude_video: "" })}
                      className="absolute top-2 right-2 p-2 bg-wedding-black/80 rounded-full hover:bg-wedding-black transition-colors"
                    >
                      <X className="w-4 h-4 text-wedding-ivory" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => triggerUpload("gratitude_video")}
                    disabled={uploading}
                    className="w-full max-w-xl aspect-video border border-dashed border-white/20 hover:border-wedding-gold/50 transition-colors flex flex-col items-center justify-center gap-3 rounded-sm"
                  >
                    {uploading && uploadingFor === "gratitude_video" ? (
                      <div className="w-8 h-8 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Video className="w-8 h-8 text-wedding-ivory/40" />
                        <span className="text-wedding-ivory/60 text-sm">Upload Gratitude Video</span>
                        <span className="text-wedding-ivory/30 text-xs">MP4 only (max 50MB)</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div>
              <Label className="form-label">Thank You Message</Label>
              <Textarea
                value={formData.gratitude_text}
                onChange={(e) => setFormData({ ...formData, gratitude_text: e.target.value })}
                placeholder="Write a heartfelt thank you message..."
                className="bg-transparent border border-white/20 focus:border-wedding-gold outline-none px-4 py-3 transition-colors duration-300 placeholder:text-white/20 text-wedding-ivory min-h-[120px] rounded-sm w-full mt-2"
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-wedding-subtle rounded-sm">
              <div>
                <span className="text-wedding-ivory">Show Gratitude Section</span>
                <p className="text-wedding-ivory/50 text-sm">Display gratitude video and message</p>
              </div>
              <Switch
                checked={formData.sections_enabled.gratitude}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  sections_enabled: { ...formData.sections_enabled, gratitude: checked }
                })}
              />
            </div>
          </motion.div>
        )}

        {/* Photo Gallery */}
        {activeSection === "gallery" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl text-wedding-ivory">Photo Gallery</h2>
                <p className="text-wedding-ivory/40 text-sm mt-1">
                  Upload your favorite wedding photos
                </p>
              </div>
              <Button
                type="button"
                onClick={() => triggerUpload("gallery")}
                disabled={uploading}
                className="btn-secondary"
              >
                {uploading && uploadingFor === "gallery" ? (
                  <div className="w-4 h-4 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Add Photo
              </Button>
            </div>
            
            {formData.gallery_images.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.gallery_images.map((image, index) => (
                  <div key={index} className="relative aspect-square bg-wedding-subtle rounded-sm overflow-hidden group">
                    <img
                      src={getMediaUrl(image)}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeGalleryImage(index)}
                      className="absolute top-2 right-2 p-1 bg-wedding-black/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4 text-wedding-ivory" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border border-dashed border-white/10 rounded-sm">
                <Camera className="w-12 h-12 text-wedding-ivory/20 mx-auto mb-3" />
                <p className="text-wedding-ivory/50">No photos yet</p>
                <p className="text-wedding-ivory/30 text-sm">Click "Add Photo" to upload</p>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-wedding-subtle rounded-sm">
              <div>
                <span className="text-wedding-ivory">Show Gallery Section</span>
                <p className="text-wedding-ivory/50 text-sm">Display photo gallery on your page</p>
              </div>
              <Switch
                checked={formData.sections_enabled.gallery}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  sections_enabled: { ...formData.sections_enabled, gallery: checked }
                })}
              />
            </div>
          </motion.div>
        )}

        {/* Timeline Events */}
        {activeSection === "timeline" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl text-wedding-ivory">Timeline Events</h2>
                <p className="text-wedding-ivory/40 text-sm mt-1">Add key moments in your story (enable "Our Story" mode in Modules)</p>
              </div>
              <Button
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  timeline_events: [...formData.timeline_events, { date: "", title: "", description: "", image: "" }]
                })}
                className="btn-secondary"
              >
                <Plus className="w-4 h-4 mr-2" />Add Event
              </Button>
            </div>

            {formData.timeline_events.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-white/10 rounded-sm">
                <p className="text-wedding-ivory/50">No timeline events yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.timeline_events.map((event, i) => (
                  <div key={i} className="p-4 bg-wedding-subtle border border-white/5 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-wedding-gold text-sm">Event {i + 1}</span>
                      <button
                        onClick={() => {
                          const events = formData.timeline_events.filter((_, idx) => idx !== i);
                          setFormData({ ...formData, timeline_events: events });
                        }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-wedding-ivory/50 text-xs">Date</Label>
                        <Input
                          value={event.date}
                          onChange={(e) => {
                            const events = [...formData.timeline_events];
                            events[i] = { ...events[i], date: e.target.value };
                            setFormData({ ...formData, timeline_events: events });
                          }}
                          placeholder="June 2020"
                          className="input-premium mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-wedding-ivory/50 text-xs">Title</Label>
                        <Input
                          value={event.title}
                          onChange={(e) => {
                            const events = [...formData.timeline_events];
                            events[i] = { ...events[i], title: e.target.value };
                            setFormData({ ...formData, timeline_events: events });
                          }}
                          placeholder="First Date"
                          className="input-premium mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-wedding-ivory/50 text-xs">Description</Label>
                      <Textarea
                        value={event.description}
                        onChange={(e) => {
                          const events = [...formData.timeline_events];
                          events[i] = { ...events[i], description: e.target.value };
                          setFormData({ ...formData, timeline_events: events });
                        }}
                        placeholder="A brief description..."
                        className="bg-transparent border border-white/20 focus:border-wedding-gold outline-none px-4 py-3 transition-colors duration-300 placeholder:text-white/20 text-wedding-ivory min-h-[80px] rounded-sm w-full mt-1"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-wedding-subtle rounded-sm">
              <div>
                <span className="text-wedding-ivory">Show Timeline Section</span>
                <p className="text-wedding-ivory/50 text-sm">Display timeline on your wedding page</p>
              </div>
              <Switch
                checked={formData.sections_enabled.timeline}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  sections_enabled: { ...formData.sections_enabled, timeline: checked }
                })}
              />
            </div>
          </motion.div>
        )}

        {/* Tree Settings */}
        {activeSection === "tree" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-xl text-wedding-ivory mb-6">Wedding Tree Settings</h2>
            
            <div className="flex items-center justify-between p-4 bg-wedding-subtle rounded-sm">
              <div>
                <span className="text-wedding-ivory">Enable Wedding Tree</span>
                <p className="text-wedding-ivory/50 text-sm">Allow guests to submit video greetings</p>
              </div>
              <Switch
                checked={formData.tree_enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, tree_enabled: checked })}
              />
            </div>

            <div>
              <Label className="form-label">Moderation Mode</Label>
              <Select
                value={formData.tree_moderation_mode}
                onValueChange={(value) => setFormData({ ...formData, tree_moderation_mode: value })}
              >
                <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wedding-charcoal border-white/10">
                  <SelectItem value="auto_publish">Auto Publish</SelectItem>
                  <SelectItem value="manual_approval">Manual Approval</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-wedding-ivory/40 text-xs mt-2">
                {formData.tree_moderation_mode === "auto_publish" 
                  ? "Guest messages will appear immediately on your tree" 
                  : "You'll need to approve messages before they appear"}
              </p>
            </div>

            <div>
              <Label className="form-label">Best Wishes Preview Count</Label>
              <p className="text-wedding-ivory/40 text-xs mb-2">
                Number of guest video thumbnails shown on the landing page
              </p>
              <Select
                value={String(formData.best_wishes_preview_count)}
                onValueChange={(value) => setFormData({ ...formData, best_wishes_preview_count: parseInt(value) })}
              >
                <SelectTrigger className="w-full bg-wedding-subtle border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wedding-charcoal border-white/10">
                  <SelectItem value="3">3 messages</SelectItem>
                  <SelectItem value="6">6 messages</SelectItem>
                  <SelectItem value="9">9 messages</SelectItem>
                  <SelectItem value="12">12 messages</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-4 bg-wedding-subtle rounded-sm">
              <div>
                <span className="text-wedding-ivory">Show Tree Preview</span>
                <p className="text-wedding-ivory/50 text-sm">Display tree preview on landing page</p>
              </div>
              <Switch
                checked={formData.sections_enabled.tree}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  sections_enabled: { ...formData.sections_enabled, tree: checked }
                })}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-wedding-subtle rounded-sm">
              <div>
                <span className="text-wedding-ivory">Show Best Wishes Section</span>
                <p className="text-wedding-ivory/50 text-sm">Display guest video thumbnails</p>
              </div>
              <Switch
                checked={formData.sections_enabled.best_wishes}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  sections_enabled: { ...formData.sections_enabled, best_wishes: checked }
                })}
              />
            </div>
          </motion.div>
        )}

        {/* Visibility */}
        {activeSection === "visibility" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-xl text-wedding-ivory mb-6">Page Visibility</h2>
            
            <div>
              <Label className="form-label">Visibility Mode</Label>
              <Select
                value={formData.visibility}
                onValueChange={(value) => setFormData({ ...formData, visibility: value })}
              >
                <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wedding-charcoal border-white/10">
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="passcode">Passcode Protected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.visibility === "passcode" && (
              <div>
                <Label className="form-label">Page Passcode</Label>
                <Input
                  type="text"
                  value={formData.passcode}
                  onChange={(e) => setFormData({ ...formData, passcode: e.target.value })}
                  placeholder="Enter a passcode"
                  className="input-premium mt-2"
                />
                <p className="text-wedding-ivory/40 text-xs mt-2">
                  Guests will need to enter this passcode to view your page
                </p>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Save Button (Bottom) */}
      <div className="flex justify-end pt-4">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
        >
          {saving ? "Saving..." : "Save All Changes"}
        </Button>
      </div>
    </div>
  );
}
